/* Shipment registration is a theme-only workflow. Shopify remains the cart source of truth. */
(() => {
  'use strict';

  if (window.ShipmentTournament) return;

  const GROUP = Object.freeze({ solo: 1, duo: 2, team: 3 });
  const REGISTRATION = Object.freeze({ solo: 'Individual', duo: 'Duo', team: 'Team' });
  const SKU = Object.freeze({ self: '3V3-SHIP-SELF', rental: '3V3-SHIP-RENT' });
  const ATTRIBUTE_KEYS = Object.freeze([
    'Shipment Event', 'Shipment Registration', 'Shipment Registration ID',
    'Shipment Registration Closes At', 'Shipment Team Name', 'Shipment Player Count',
    'Shipment Player 1 Name', 'Shipment Player 2 Name', 'Shipment Player 3 Name',
    'Shipment Player 1 Package', 'Shipment Player 2 Package', 'Shipment Player 3 Package',
  ]);
  const productDisposers = new WeakMap();

  const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const normalized = (value) => String(value || '').trim().toLocaleLowerCase();

  function makeRegistration(input, id, closesAt) {
    const mode = input.mode;
    const size = GROUP[mode];
    if (!size || !id) throw new Error('A supported registration mode and stable id are required.');
    const names = input.names.slice(0, size).map((name) => String(name || '').trim());
    const packages = input.packages.slice(0, size);
    if (names.length !== size || names.some((name) => !name) || packages.length !== size || packages.some((pkg) => !SKU[pkg])) {
      throw new Error('The frozen registration is incomplete.');
    }
    const teamName = mode === 'solo' ? 'Needs placement' : String(input.teamName || '').trim();
    const registration = REGISTRATION[mode];
    const attributes = {
      'Shipment Event': '3v3 Shipment',
      'Shipment Registration': registration,
      'Shipment Registration ID': id,
      'Shipment Registration Closes At': closesAt,
      'Shipment Team Name': teamName,
      'Shipment Player Count': String(size),
    };
    const items = names.map((name, index) => {
      const slot = index + 1;
      const pkg = packages[index];
      attributes['Shipment Player ' + slot + ' Name'] = name;
      attributes['Shipment Player ' + slot + ' Package'] = pkg === 'self' ? 'Self-Equipped' : 'Rental';
      const properties = {
        'Player Name': name,
        Registration: registration,
        _registration_id: id,
        _registration_slot: String(slot),
        _registration_size: String(size),
        _loadout: pkg,
      };
      if (teamName) properties['Team Name'] = teamName;
      return { id: Number(input.variantIds[pkg]), quantity: 1, properties };
    });
    return { id, mode, registration, size, teamName, names, packages, closesAt, acknowledged: Boolean(input.acknowledged), attributes, items, createdAt: Date.now() };
  }

  function isShipmentLine(item, handle) {
    const props = item && item.properties || {};
    const sku = String(item && item.sku || '').toUpperCase();
    return (handle && item && item.handle === handle) || sku === SKU.self || sku === SKU.rental ||
      Boolean(props._registration_id);
  }

  function inspectCart(cart, handle) {
    const attributes = cart && cart.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};
    const shipmentAttributes = ATTRIBUTE_KEYS.filter((key) => attributes[key] !== undefined && attributes[key] !== null && attributes[key] !== '');
    const lines = (cart && Array.isArray(cart.items) ? cart.items : []).filter((item) => isShipmentLine(item, handle));
    const present = shipmentAttributes.length > 0 || lines.length > 0;
    if (!present) return { present: false, valid: true, lines: [], attributes, shipmentAttributes, errors: [] };

    const errors = [];
    const id = String(attributes['Shipment Registration ID'] || '');
    const registration = String(attributes['Shipment Registration'] || '');
    const mode = registration === 'Individual' ? 'solo' : registration === 'Duo' ? 'duo' : registration === 'Team' ? 'team' : '';
    const size = GROUP[mode] || 0;
    if (!id || !size || attributes['Shipment Event'] !== '3v3 Shipment') errors.push('registration attributes are missing');
    if (!attributes['Shipment Registration Closes At'] || !Number.isFinite(Date.parse(attributes['Shipment Registration Closes At']))) errors.push('the registration close time is missing');
    if (String(attributes['Shipment Player Count'] || '') !== String(size)) errors.push('the player count does not match the registration');
    if (lines.length !== size) errors.push('the player line count does not match the registration');
    if (mode === 'solo' && String(attributes['Shipment Team Name'] || '') !== 'Needs placement') errors.push('the individual placement value is missing');
    if (mode !== 'solo' && String(attributes['Shipment Team Name'] || '').trim().length < 2) errors.push('the team name is missing');
    for (let slot = size + 1; slot <= 3; slot += 1) {
      if (attributes['Shipment Player ' + slot + ' Name'] || attributes['Shipment Player ' + slot + ' Package']) errors.push(`inactive player ${slot} attributes remain`);
    }

    const slots = new Map();
    lines.forEach((item) => {
      const props = item.properties || {};
      const slot = Number(props._registration_slot);
      const sku = String(item.sku || '').toUpperCase();
      if (!Number.isInteger(slot) || slot < 1 || slot > size || slots.has(slot)) errors.push('player slots are missing or duplicated');
      else slots.set(slot, item);
      if (Number(item.quantity) !== 1) errors.push('a player line quantity is not one');
      if (!id || String(props._registration_id || '') !== id) errors.push('player lines do not share the registration id');
      if (String(props.Registration || '') !== registration) errors.push('a player line has the wrong registration type');
      if (sku !== SKU.self && sku !== SKU.rental) errors.push('a player line has an unrecognized package');
      if (Number(props._registration_size) !== size) errors.push('a player line has the wrong group size');
      if (String(props['Team Name'] || '') !== String(attributes['Shipment Team Name'] || '')) errors.push('the team name does not match');
    });

    for (let slot = 1; slot <= size; slot += 1) {
      const item = slots.get(slot);
      const name = String(attributes['Shipment Player ' + slot + ' Name'] || '').trim();
      const packageName = String(attributes['Shipment Player ' + slot + ' Package'] || '');
      const packageSku = packageName === 'Self-Equipped' ? SKU.self : packageName === 'Rental' ? SKU.rental : '';
      if (!name || !packageSku) errors.push(`player ${slot} attributes are missing`);
      if (item && String(item.properties && item.properties['Player Name'] || '').trim() !== name) errors.push(`player ${slot} name does not match`);
      if (item && String(item.sku || '').toUpperCase() !== packageSku) errors.push(`player ${slot} package does not match`);
    }
    return { present: true, valid: errors.length === 0, lines, attributes, shipmentAttributes, errors, id, registration, mode, size };
  }

  function attemptLineSlots(cart, attempt) {
    const matching = (cart && Array.isArray(cart.items) ? cart.items : []).filter((item) => {
      const props = item.properties || {};
      return String(props._registration_id || '') === String(attempt.id);
    });
    const slots = new Map();
    for (const item of matching) {
      const slot = Number(item.properties && item.properties._registration_slot);
      if (!Number.isInteger(slot) || slot < 1 || slot > attempt.size || slots.has(slot)) {
        return { matching, slots, error: true };
      }
      slots.set(slot, item);
    }
    return { matching, slots, error: false };
  }

  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const value = Number(cents) / 100;
    const whole = Number.isInteger(value);
    const withSep = (number, decimals) => number.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return withSep(Math.round(value), 0);
      if (key === 'amount_with_comma_separator') return withSep(value, whole ? 0 : 2).replace('.', ',');
      return withSep(value, whole ? 0 : 2);
    });
  }

  function calculateTotal(packages, prices) {
    return packages.reduce((total, pkg) => total + Number(prices[pkg] || 0), 0);
  }

  function isRegistrationOpen(config, now) {
    const currentTime = Number.isFinite(now) ? now : Date.now();
    return Boolean(config.manualOpen && config.mappingValid && config.deadlineValid && Number.isFinite(config.closesAt) && currentTime < config.closesAt);
  }

  function randomRegistrationId() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const bytes = new Uint8Array(16);
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    return `SHP-${Array.from(bytes, (byte) => chars[byte % chars.length]).join('')}`;
  }

  function jsonRequest(path, body) {
    return fetch(`${routesRoot()}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || `Cart request failed (${response.status}).`);
      return data;
    });
  }

  const getCart = () => jsonRequest('cart.js');

  function getStoredAttempt(key) {
    try {
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      const attempt = JSON.parse(raw);
      if (!attempt || !attempt.id || !Array.isArray(attempt.items) || !attempt.attributes) return null;
      return attempt;
    } catch (_) { return null; }
  }

  function storeAttempt(key, attempt) {
    try { window.sessionStorage.setItem(key, JSON.stringify(attempt)); return true; }
    catch (_) { return false; }
  }

  function clearAttempt(key) {
    try { window.sessionStorage.removeItem(key); } catch (_) { /* Storage can be unavailable. */ }
  }

  function mergeRegistrationAttributes(existing, registration) {
    const attributes = Object.assign({}, existing || {});
    ATTRIBUTE_KEYS.forEach((key) => { delete attributes[key]; });
    return Object.assign(attributes, registration && registration.attributes || {});
  }

  function clearRegistrationAttributes(existing) {
    const attributes = Object.assign({}, existing || {});
    ATTRIBUTE_KEYS.forEach((key) => { attributes[key] = ''; });
    return attributes;
  }

  function initProduct(root) {
    const data = root.dataset;
    const form = root.querySelector('[data-st-form]');
    const closed = root.querySelector('[data-st-closed]');
    const cartLink = root.querySelector('[data-st-closed-cart-link]');
    const attemptKey = data.attemptKey || 'shipment:attempt:3v3-shipment-tournament';
    const config = {
      handle: data.productHandle || '3v3-shipment-tournament',
      variantIds: { self: Number(data.selfId), rental: Number(data.rentId) },
      prices: { self: Number(data.selfPrice), rental: Number(data.rentPrice) },
      available: { self: data.selfAvailable === 'true', rental: data.rentAvailable === 'true' },
      money: data.moneyFormat,
      closesAt: Date.parse(data.closesAt || ''),
      manualOpen: data.manualOpen === 'true',
      mappingValid: data.mappingValid === 'true',
      deadlineValid: data.deadlineValid === 'true',
      afterAdd: data.afterAdd || 'checkout',
    };
    const tr = (name, fallback) => data[`msg${name[0].toUpperCase()}${name.slice(1)}`] || fallback;
    const label = (name, fallback) => data[`label${name[0].toUpperCase()}${name.slice(1)}`] || fallback;
    const money = (cents) => formatMoney(cents, config.money);
    const formError = form && form.querySelector('[data-st-form-error]');

    const closeForm = (message) => {
      if (form) form.hidden = true;
      if (closed) {
        closed.hidden = false;
        const text = closed.querySelector('[data-st-closed-message]');
        if (text && message) text.textContent = message;
      }
    };
    const isOpen = () => isRegistrationOpen(config);
    let deadlineTimeout = null;
    let deadlineInterval = null;
    const onVisibilityChange = () => { if (!document.hidden) checkDeadline(); };
    const onWindowFocus = () => checkDeadline();
    const cleanupDeadline = () => {
      if (deadlineTimeout !== null) window.clearTimeout(deadlineTimeout);
      if (deadlineInterval !== null) window.clearInterval(deadlineInterval);
      deadlineTimeout = null;
      deadlineInterval = null;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onWindowFocus);
    };
    const checkDeadline = () => {
      if (!isOpen()) {
        const message = !config.mappingValid ? tr('missingMapping', '') : !config.deadlineValid ? tr('invalidDeadline', '') : config.manualOpen ? tr('deadlineError', '') : data.closedMessage || '';
        closeForm(message);
        cleanupDeadline();
      }
    };
    productDisposers.set(root, cleanupDeadline);
    checkDeadline();
    if (form && isOpen()) {
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('focus', onWindowFocus);
      deadlineTimeout = window.setTimeout(checkDeadline, Math.min(config.closesAt - Date.now() + 20, 2147483647));
      deadlineInterval = window.setInterval(checkDeadline, 60000);
    }

    if (!form || form.hidden) {
      if (cartLink) {
        getCart().then((cart) => { cartLink.hidden = !inspectCart(cart, config.handle).present; }).catch(() => {});
      }
      productDisposers.set(root, cleanupDeadline);
      return;
    }

    const elements = {
      teamWrap: root.querySelector('[data-st-teamname]'),
      team: form.elements.team_name,
      rows: Array.from(form.querySelectorAll('[data-st-player]')),
      callouts: Array.from(form.querySelectorAll('[data-st-callout]')),
      ackWrap: root.querySelector('[data-st-ack]'),
      ack: form.elements.ack,
      ackText: root.querySelector('[data-st-ack-text]'),
      total: root.querySelector('[data-st-total]'),
      totalLabel: root.querySelector('[data-st-total-label]'),
      breakdown: root.querySelector('[data-st-breakdown]'),
      cta: root.querySelector('[data-st-cta]'),
      spinner: root.querySelector('[data-st-spinner]'),
    };
    const formControls = Array.from(form.querySelectorAll('input, button'));
    const initiallyDisabled = new Map(formControls.map((control) => [control, control.disabled]));
    let savedAttempt = getStoredAttempt(attemptKey);
    let attemptFrozen = Boolean(savedAttempt);
    const mode = () => (form.querySelector('input[name="mode"]:checked') || {}).value || 'team';
    const packageOf = (slot) => (form.querySelector(`input[name="pkg_${slot}"]:checked`) || {}).value || '';
    const activeRows = () => elements.rows.slice(0, GROUP[mode()] || GROUP.team);

    if (savedAttempt) {
      const modeRadio = form.querySelector('input[name="mode"][value="' + savedAttempt.mode + '"]');
      if (modeRadio) modeRadio.checked = true;
      if (elements.team) elements.team.value = savedAttempt.mode === 'solo' ? '' : savedAttempt.teamName || '';
      (savedAttempt.names || []).forEach((name, index) => {
        const player = form.elements['player_' + (index + 1)];
        if (player) player.value = name;
        const packageRadio = form.querySelector('input[name="pkg_' + (index + 1) + '"][value="' + savedAttempt.packages[index] + '"]');
        if (packageRadio) packageRadio.checked = true;
      });
      if (elements.ack) elements.ack.checked = Boolean(savedAttempt.acknowledged);
    }

    let previousMode = mode();
    function render() {
      const currentMode = mode();
      if (currentMode !== previousMode && elements.ack) {
        elements.ack.checked = false;
        setError('ack', '');
      }
      previousMode = currentMode;
      const size = GROUP[currentMode] || GROUP.team;
      if (elements.teamWrap) elements.teamWrap.hidden = currentMode === 'solo';
      elements.rows.forEach((row, index) => {
        row.hidden = index >= size;
        const playerLabel = row.querySelector('[data-st-player-label]');
        if (playerLabel) {
          playerLabel.textContent = currentMode === 'solo' ? label('soloName', '') : index === 0 ? `${label('player', '')} 1 ${label('captainSuffix', '')}` : `${label('player', '')} ${index + 1}`;
        }
        const packageField = row.querySelector('.st-player__pkg');
        if (packageField) packageField.hidden = false;
      });
      elements.callouts.forEach((callout) => { callout.hidden = callout.dataset.stCallout !== currentMode; });
      if (elements.ackWrap) elements.ackWrap.hidden = currentMode === 'team';
      if (elements.ackText) elements.ackText.textContent = currentMode === 'duo' ? data.duoAck : currentMode === 'solo' ? data.soloAck : '';
      const packages = activeRows().map((_, index) => packageOf(index + 1));
      const counts = { self: packages.filter((pkg) => pkg === 'self').length, rental: packages.filter((pkg) => pkg === 'rental').length };
      const total = calculateTotal(packages, config.prices);
      if (elements.total) elements.total.textContent = money(total);
      if (elements.totalLabel) elements.totalLabel.textContent = size === 1 ? label('totalOne', '') : label('totalMany', '').replace('COUNT', String(size));
      const parts = [];
      if (counts.self) parts.push(`${counts.self} × ${label('selfShort', '')} ${money(counts.self * config.prices.self)}`);
      if (counts.rental) parts.push(`${counts.rental} × ${label('rentalShort', '')} ${money(counts.rental * config.prices.rental)}`);
      if (elements.breakdown) elements.breakdown.textContent = size > 1 ? parts.join(' + ') : '';
      const verb = currentMode === 'team' ? label('registerTeam', '') : currentMode === 'duo' ? label('registerDuo', '') : label('registerIndividual', '');
      if (elements.cta && !attemptFrozen && !elements.cta.disabled) elements.cta.querySelector('[data-st-cta-label]').textContent = `${verb} — ${money(total)}`;
      renderDuplicates();
    }

    function setError(name, message) {
      const slot = root.querySelector(`[data-st-err="${name}"]`);
      if (slot) slot.textContent = message || '';
      const packageMatch = /^package_(\d+)$/.exec(name);
      const field = packageMatch ? form.querySelector(`input[name="pkg_${packageMatch[1]}"]`) : form.elements[name];
      const wrapper = packageMatch ? field && field.closest('.st-player__pkg') : field && field.closest && field.closest('.st-input');
      if (wrapper) wrapper.classList.toggle('is-invalid', Boolean(message));
      if (field && field.setAttribute) {
        if (message) field.setAttribute('aria-invalid', 'true'); else field.removeAttribute('aria-invalid');
      }
    }

    function renderDuplicates() {
      const byName = new Map();
      activeRows().forEach((_, index) => {
        const field = form.elements[`player_${index + 1}`];
        const value = normalized(field && field.value);
        if (value) byName.set(value, (byName.get(value) || 0) + 1);
      });
      elements.rows.forEach((row, index) => {
        const warning = row.querySelector('[data-st-warning]');
        const field = form.elements[`player_${index + 1}`];
        const duplicate = index < GROUP[mode()] && normalized(field && field.value) && byName.get(normalized(field.value)) > 1;
        if (warning) warning.hidden = !duplicate;
        if (field && field.setAttribute) {
          if (!duplicate && !root.querySelector(`[data-st-err="player_${index + 1}"]`)?.textContent) field.removeAttribute('aria-invalid');
        }
      });
    }

    function validate() {
      const currentMode = mode();
      const errors = [];
      ['team_name', 'player_1', 'player_2', 'player_3', 'package_1', 'package_2', 'package_3', 'ack'].forEach((name) => setError(name, ''));
      if (!isOpen()) {
        checkDeadline();
        errors.push(['team_name', tr('deadlineError', '')]);
      }
      if (!config.mappingValid || !config.variantIds.self || !config.variantIds.rental) errors.push(['team_name', tr('missingMapping', '')]);
      if (currentMode !== 'solo') {
        const team = elements.team.value.trim();
        if (team.length < 2 || team.length > 30) errors.push(['team_name', tr('teamLength', '')]);
      }
      const names = activeRows().map((_, index) => String(form.elements[`player_${index + 1}`].value || '').trim());
      names.forEach((name, index) => {
        if (name.length < 2 || name.length > 40) errors.push([`player_${index + 1}`, tr('playerLength', '')]);
      });
      if (currentMode !== 'team' && !elements.ack.checked) errors.push(['ack', tr('ack', '')]);
      activeRows().forEach((_, index) => {
        const selected = packageOf(index + 1);
        if (!['self', 'rental'].includes(selected) || !config.available[selected]) errors.push([`package_${index + 1}`, tr('package', '')]);
      });
      errors.forEach(([name, message]) => setError(name, message));
      if (errors.length) {
        const firstName = errors[0][0];
        const packageMatch = /^package_(\d+)$/.exec(firstName);
        const first = packageMatch ? form.querySelector(`input[name="pkg_${packageMatch[1]}"]`) : form.elements[firstName];
        const focusTarget = first && first.focus ? first : first && first[0];
        if (focusTarget && focusTarget.focus) focusTarget.focus();
      }
      return errors.length === 0;
    }

    function freezeForm() {
      return {
        mode: mode(),
        teamName: elements.team.value.trim(),
        names: activeRows().map((_, index) => form.elements[`player_${index + 1}`].value.trim()),
        packages: activeRows().map((_, index) => packageOf(index + 1)),
        acknowledged: mode() === 'team' || Boolean(elements.ack && elements.ack.checked),
        variantIds: config.variantIds,
      };
    }

    function setSubmitting(submitting, caption) {
      formControls.forEach((control) => {
        const frozenControl = control !== elements.cta && attemptFrozen;
        control.disabled = Boolean(submitting || frozenControl || initiallyDisabled.get(control));
      });
      if (elements.spinner) elements.spinner.hidden = !submitting;
      const text = elements.cta && elements.cta.querySelector('[data-st-cta-label]');
      if (text && caption) text.textContent = caption;
    }

    function registrationInCartError(discardableAttempt = false) {
      const error = new Error(tr('registrationInCart', ''));
      error.code = 'SHIPMENT_REGISTRATION_IN_CART';
      error.discardableAttempt = discardableAttempt;
      return error;
    }

    async function resumeAttempt(attempt) {
      let cart = await getCart();
      let inspection = inspectCart(cart, config.handle);
      const existing = attemptLineSlots(cart, attempt);
      if (existing.error) throw new Error(tr('genericError', ''));
      if (inspection.present && inspection.id && inspection.id !== attempt.id) {
        throw registrationInCartError(existing.matching.length === 0);
      }
      if (inspection.present && inspection.id === attempt.id && inspection.valid) {
        clearAttempt(attemptKey);
        window.location.href = `${routesRoot()}${config.afterAdd === 'cart' ? 'cart' : 'checkout'}`;
        return;
      }
      const otherGroupLines = (cart.items || []).filter((item) => isShipmentLine(item, config.handle) && String(item.properties && item.properties._registration_id || '') !== attempt.id);
      if (otherGroupLines.length) throw registrationInCartError(existing.matching.length === 0);

      const remaining = attempt.items.filter((item) => !existing.slots.has(Number(item.properties._registration_slot)));
      if (!isOpen() && remaining.length) throw new Error(tr('deadlineError', ''));
      if (remaining.length) {
        await jsonRequest('cart/add.js', { items: remaining });
      }

      cart = await getCart();
      inspection = inspectCart(cart, config.handle);
      if (inspection.present && inspection.id && inspection.id !== attempt.id) throw registrationInCartError(false);
      const mergedAttributes = mergeRegistrationAttributes(cart.attributes || {}, attempt);
      await jsonRequest('cart/update.js', { attributes: mergedAttributes });

      cart = await getCart();
      inspection = inspectCart(cart, config.handle);
      if (!inspection.present || inspection.id !== attempt.id || !inspection.valid) throw new Error(tr('genericError', ''));
      clearAttempt(attemptKey);
      window.location.href = `${routesRoot()}${config.afterAdd === 'cart' ? 'cart' : 'checkout'}`;
    }

    let submitting = false;
    async function submit(event) {
      event.preventDefault();
      if (submitting) return;
      if (formError) formError.hidden = true;
      checkDeadline();
      let newlyCreatedAttempt = false;
      try {
        let attempt = savedAttempt;
        if (!attempt) {
          if (!validate()) return;
          attempt = makeRegistration(freezeForm(), randomRegistrationId(), data.closesAt);
          if (!storeAttempt(attemptKey, attempt)) throw new Error(tr('genericError', ''));
          attemptFrozen = true;
          savedAttempt = attempt;
          newlyCreatedAttempt = true;
        }
        submitting = true;
        setSubmitting(true, label('registering', ''));
        if (savedAttempt && formError) {
          formError.textContent = tr('savedAttempt', '');
          formError.hidden = false;
        }
        if (navigator.locks && navigator.locks.request) {
          await navigator.locks.request('shipment-registration-3v3-shipment-tournament', { mode: 'exclusive' }, () => resumeAttempt(attempt));
        } else {
          await resumeAttempt(attempt);
        }
      } catch (error) {
        const discardAttempt = newlyCreatedAttempt && error && error.code === 'SHIPMENT_REGISTRATION_IN_CART' && error.discardableAttempt;
        if (discardAttempt) {
          clearAttempt(attemptKey);
          savedAttempt = null;
          attemptFrozen = false;
        }
        if (formError) {
          const message = error && error.message || '';
          const visible = [tr('deadlineError', ''), tr('registrationInCart', ''), tr('genericError', '')];
          formError.textContent = visible.includes(message) ? message : tr('genericError', '');
          formError.hidden = false;
        }
        const link = root.querySelector('[data-st-form-cart-link]');
        if (link) link.hidden = false;
        submitting = false;
        setSubmitting(false, getStoredAttempt(attemptKey) ? label('finish', '') : label('retry', ''));
        if (discardAttempt) render();
      }
    }

    const onChange = (event) => {
      const target = event.target;
      if (target.name && (target.name.startsWith('player_') || target.name === 'team_name' || target.name === 'ack')) setError(target.name, '');
      if (target.name && target.name.startsWith('pkg_')) setError(target.name.replace('pkg_', 'package_'), '');
      render();
    };
    const onInput = (event) => {
      const target = event.target;
      if (target.name && (target.name.startsWith('player_') || target.name === 'team_name')) {
        setError(target.name, '');
        renderDuplicates();
      }
    };
    form.addEventListener('change', onChange);
    form.addEventListener('input', onInput);
    form.addEventListener('submit', submit);
    if (savedAttempt) setSubmitting(false, label('finish', ''));
    productDisposers.set(root, () => {
      cleanupDeadline();
      form.removeEventListener('change', onChange);
      form.removeEventListener('input', onInput);
      form.removeEventListener('submit', submit);
    });
    render();
  }

  function buildCartSummary(root, inspection) {
    const attrs = inspection.attributes;
    const team = root.querySelector('[data-st-cart-team]');
    const roster = root.querySelector('[data-st-cart-roster]');
    const remove = root.querySelector('[data-st-cart-remove]');
    const again = root.querySelector('[data-st-cart-again]');
    const warning = root.querySelector('[data-st-cart-warning]');
    const status = root.querySelector('[data-st-cart-status]');
    if (!inspection.present) {
      root.hidden = true;
      if (remove) remove.hidden = true;
      if (again) again.hidden = true;
      return;
    }
    root.hidden = false;
    if (team) {
      const teamName = String(attrs['Shipment Team Name'] || '').trim();
      team.textContent = teamName ? `${root.dataset.labelTeam || ''}: ${teamName}` : String(attrs['Shipment Registration'] || '');
      team.hidden = !team.textContent;
    }
    if (roster) {
      roster.replaceChildren();
      const count = Number(inspection.size) || 3;
      for (let slot = 1; slot <= count; slot += 1) {
        const name = attrs['Shipment Player ' + slot + ' Name'];
        if (!name) continue;
        const row = document.createElement('li');
        const packageName = attrs['Shipment Player ' + slot + ' Package'];
        const pkg = packageName === 'Rental' ? root.dataset.labelRental : packageName === 'Self-Equipped' ? root.dataset.labelSelf : '';
        row.textContent = pkg ? `${name} · ${pkg}` : String(name);
        roster.appendChild(row);
      }
      roster.hidden = roster.children.length === 0;
    }
    if (warning) {
      warning.textContent = inspection.valid ? '' : root.dataset.msgMismatch || '';
      warning.hidden = inspection.valid;
    }
    if (remove) {
      remove.hidden = false;
      remove.disabled = false;
      remove.textContent = root.dataset.labelRemove || '';
    }
    if (again) again.hidden = inspection.valid;
    if (status && inspection.valid) status.textContent = '';
  }

  async function refreshCartGuards(cartSnapshot) {
    const roots = Array.from(document.querySelectorAll('[data-st-cart-guard]'));
    if (!roots.length) return { checked: false, inspection: null };
    const buttons = Array.from(document.querySelectorAll('[data-shipment-checkout]'));
    buttons.forEach((button) => { button.disabled = true; button.setAttribute('aria-disabled', 'true'); });
    try {
      const hasSnapshot = cartSnapshot && Array.isArray(cartSnapshot.items) && cartSnapshot.attributes && typeof cartSnapshot.attributes === 'object';
      const cart = hasSnapshot ? cartSnapshot : await getCart();
      const inspection = inspectCart(cart, roots[0].dataset.stCartHandle || '3v3-shipment-tournament');
      roots.forEach((root) => {
        root.dataset.stCartReady = '1';
        buildCartSummary(root, inspection);
        root.dataset.stCartValid = inspection.present && inspection.valid ? 'true' : 'false';
        const remove = root.querySelector('[data-st-cart-remove]');
        if (remove && !remove.dataset.stRemoveBound) {
          remove.dataset.stRemoveBound = '1';
          remove.addEventListener('click', () => removeRegistration(root));
        }
      });
      if (!inspection.present || inspection.valid) {
        buttons.forEach((button) => { button.disabled = false; button.removeAttribute('aria-disabled'); });
      } else {
        roots.forEach((root) => {
          const warning = root.querySelector('[data-st-cart-warning]');
          if (warning) warning.textContent = root.dataset.msgMismatch || '';
        });
      }
      return { checked: true, inspection };
    } catch (_) {
      roots.forEach((root) => {
        root.hidden = false;
        const warning = root.querySelector('[data-st-cart-warning]');
        if (warning) { warning.textContent = root.dataset.msgEmptyCart || ''; warning.hidden = false; }
      });
      return { checked: false, inspection: null };
    }
  }

  function onCartUpdated(event) {
    refreshCartGuards(event && event.detail && event.detail.cart);
  }

  async function removeRegistration(root) {
    const button = root.querySelector('[data-st-cart-remove]');
    const status = root.querySelector('[data-st-cart-status]');
    const warning = root.querySelector('[data-st-cart-warning]');
    if (button) { button.disabled = true; button.textContent = root.dataset.labelRemoving || ''; }
    if (warning) warning.hidden = true;
    try {
      const cart = await getCart();
      const inspection = inspectCart(cart, root.dataset.stCartHandle || '3v3-shipment-tournament');
      const updates = {};
      inspection.lines.forEach((item) => { updates[item.key] = 0; });
      const attributes = clearRegistrationAttributes(cart.attributes || {});
      await jsonRequest('cart/update.js', { updates, attributes });
      const after = await getCart();
      if (inspectCart(after, root.dataset.stCartHandle || '3v3-shipment-tournament').present) throw new Error(root.dataset.msgRemoveError || '');
      clearAttempt(root.dataset.stAttemptKey || 'shipment:attempt:3v3-shipment-tournament');
      if (status) status.textContent = root.dataset.msgRemoved || '';
      root.hidden = true;
      window.location.reload();
    } catch (_) {
      if (warning) { warning.textContent = root.dataset.msgRemoveError || ''; warning.hidden = false; }
      if (button) { button.disabled = false; button.textContent = root.dataset.labelRemove || ''; }
    }
  }

  function initCart() {
    let needsRefresh = false;
    document.querySelectorAll('[data-st-cart-guard]').forEach((root) => {
      if (root.dataset.stCartReady) return;
      root.dataset.stCartReady = '1';
      needsRefresh = true;
    });
    if (needsRefresh) refreshCartGuards();
  }

  function installCheckoutGuard() {
    if (document.documentElement.dataset.stCheckoutGuard) return;
    document.documentElement.dataset.stCheckoutGuard = '1';
    const submitting = new WeakSet();
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      const submitter = event.submitter;
      if (!form || !form.querySelector('[name="checkout"]') && !(submitter && submitter.name === 'checkout')) return;
      if (!document.querySelector('[data-st-cart-guard]')) return;
      if (submitting.has(form)) { submitting.delete(form); return; }
      event.preventDefault();
      const result = await refreshCartGuards();
      const roots = Array.from(document.querySelectorAll('[data-st-cart-guard]'));
      if (!result.checked) {
        const status = roots[0] && roots[0].querySelector('[data-st-cart-status]');
        if (status) status.textContent = roots[0].dataset.msgEmptyCart || '';
        return;
      }
      if (result.inspection && result.inspection.present && !result.inspection.valid) return;
      submitting.add(form);
      if (form.requestSubmit) form.requestSubmit(submitter || undefined);
      else form.submit();
    }, true);
  }

  function boot() {
    document.querySelectorAll('[data-st]').forEach((root) => {
      if (root.dataset.stReady) return;
      root.dataset.stReady = '1';
      initProduct(root);
    });
    initCart();
    installCheckoutGuard();
  }

  function unloadProductSection(event) {
    const scope = event.target;
    const roots = [];
    if (scope && scope.matches && scope.matches('[data-st]')) roots.push(scope);
    if (scope && scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-st]'));
    roots.forEach((root) => {
      const dispose = productDisposers.get(root);
      if (dispose) dispose();
      delete root.dataset.stReady;
    });
  }

  window.ShipmentTournament = { makeRegistration, inspectCart, attemptLineSlots, calculateTotal, isRegistrationOpen, formatMoney, isShipmentLine, mergeRegistrationAttributes, clearRegistrationAttributes, ATTRIBUTE_KEYS };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', unloadProductSection);
  document.addEventListener('shopify:section:reorder', boot);
  document.addEventListener('cart:updated', onCartUpdated);
})();
